import { useEffect, useState } from 'react';
import { connect, RootStateOrAny } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { Button, Table, Modal } from 'antd';

import { ErrorResponse } from 'utils/types';
import notifier from 'utils/notifier';
import { formatDate, getDisplayCompanyId } from 'utils/helpers';
import { ClaimList } from 'utils/enums';

import { getSubmissionFile, getSubmissionFileUsingEdifecs } from 'store/claim/actions';
import { QuestionCircleOutlined } from '@ant-design/icons';
import SubmittedBatchClaimsForm from '../SubmittedBatchClaimsTable/SubmittedBatchClaimsForm';

interface SubmittedBatchClaimsTableProps {
  getSubmitted(searchObject: Object): any,
  isFetching: boolean,
  isSearching: boolean,
  getSubmissionFile(submissionId: string, fileType: string): any,
  getSubmissionFileUsingEdifecs(submissionId: string, fileType: string, refNum: string, ipa: string): any,
  isFetchingSubmissionFile: boolean,
};

const SubmittedBatchClaimsTable = (props: SubmittedBatchClaimsTableProps) => {

  // TODO: Select to Download

  const [submittedBatchClaims, setSubmittedBatchClaims] = useState<any[]>([]);
  const [legendClicked, setLegendClicked] = useState(false);
  const [submittedDate, setSubmittedDate] = useState('');

  const showLegendText = () => {
    Modal.info(
      {
        title: <><b>Status Legend</b></>,
        content: <>
                  <span>
                    <b>Processed:</b> Batch claim processed, click "View Report" for details<br/>
                  </span>
                  <span>
                    <b>Processing:</b> Batch claim under initial review<br/>
                  </span>
                  <span>
                    <b>Invalid:</b> Incorrect batch claim, click "Invalid" under the Status column of the batch claim record to see reason<br/>
                  </span>                   
                </>,
        type: "info",
        okText: "OK",
        width: '850px',
        maskClosable: false,
        onOk: () => {
          setLegendClicked(false); // Reset the flag when the modal is closed
        },
      });
  }  

  const getStatusLegend = () => (
      <Button 
        type="link" 
        className="help-link links"
        style={{ padding: 0, border: 'none', verticalAlign: 'top'}} // Adjust the line height       
      >
        <span
          onClick={(e) => {
            e.stopPropagation(); // Prevent the table header click event
            setLegendClicked(true);
            showLegendText();
          }} 
        >
          <QuestionCircleOutlined style={{ fontSize: '14px',color: '#1890ff', verticalAlign: 'top'}}/>
        </span>
          
      </Button>    
  );

  const getSubmissionFile = (subId: string, fileType: string, refNum: string, ipa: string) => {
    if (refNum === '' && ipa === '') {
      return props.getSubmissionFile(subId, fileType)
        .then((response: ArrayBuffer) => {
          const file = new Blob([response], { type: 'text/plain' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL, "_blank");
        })
        .catch((err: ErrorResponse) => {
          notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        });
    } else {
      return props.getSubmissionFileUsingEdifecs(subId, fileType, refNum, ipa)
      .then((response: ArrayBuffer) => {
        if (response.byteLength > 0) {           
          let blob = new Blob([response], {type: "application/pdf"});
          const fileURL = URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.download = "Batch Report";
          anchor.href = fileURL;
          anchor.click();
        } else {
          return props.getSubmissionFile(subId, fileType)
          .then((response: ArrayBuffer) => {
            const file = new Blob([response], { type: 'text/plain' });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL, "_blank");
          })
          .catch((err: ErrorResponse) => {
            notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
          });
        }
        notifier.success("File exported successfully.");
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
    }    
  };

  const columns = [
    {
      title: 'Date Submitted',
      key: 'RmiDateSubmit',
      dataIndex: 'RmiDateSubmit',
      sorter: (a: any, b: any) => a.RmiDateSubmit.localeCompare(b.RmiDateSubmit),
      render: (value: string) => formatDate(value, 'MM/DD/YYYY')
    },
    {
      title: 'IPA',
      key: 'RmiCompanyId',
      dataIndex: 'RmiCompanyId',
      render: (text: string) => getDisplayCompanyId(text)
    },
    {
      title: 'Reference No.',
      key: 'RmiReferenceNo',
      dataIndex: 'RmiReferenceNo',
      sorter: (a: any, b: any) => a.RmiReferenceNo.localeCompare(b.RmiReferenceNo),
    },
    {
      title: 'Filename',
      key: 'RmiOriginalFilename',
      dataIndex: 'RmiOriginalFilename',
      sorter: (a: any, b: any) => a.RmiOriginalFilename.localeCompare(b.RmiOriginalFilename),
      render: (text: string, record: any) => <Button onClick={() => getSubmissionFile(record.RmiSubmissionID, '', '', '')} type="link">{text}</Button>
    },
    {      
      title: (
        <>
          Status {getStatusLegend()}
        </>
      ),
      key: 'RmiStatus',
      dataIndex: 'RmiStatus',
      sorter: (a: any, b: any) => {
        if (legendClicked) {
          return 0; // Prevent sorting when legend is clicked
        }
        a = a.RmiStatus || '';
        b = b.RmiStatus || '';
        return a.localeCompare(b);
      },
      render: (_: string, record: any) => getStatus(record)
    },
    {
      title: 'Result',
      key: 'RmiResult',
      dataIndex: 'RmiResult',
      render: (_: any, record: any) => record.RmiStatus.toUpperCase() === 'PROCESSED' && <Button onClick={() => getSubmissionFile(record.RmiSubmissionID, record.RmiNeedAcknowledge == 'NEED ACKLEDGE' ? 'ResultFileNew' : 'ResultFile', record.RmiReferenceNo, record.RmiCompanyId)} type="link">View Report</Button>
    },
  ];

  const getStatus = (record: any) => {
    const status = record.RmiStatus;
    const pipeIndex = status?.indexOf("|");
    if (pipeIndex && pipeIndex > 0) {
      const error = status?.substring(pipeIndex + 1, status?.length);
      const batchClaimStatus = status?.toUpperCase().substring(0, pipeIndex);
      return <a onClick={()=> showBatchClaimInvalidDescription(error)}>{batchClaimStatus}</a>
    }     
    return status;
  }

  const showBatchClaimInvalidDescription = (error: string) => {
    return Modal.info(
      {
        title: "Invalid Batch Claim",
        content: error,
        type: "info",
        maskClosable: false,
      });
  }

  return (
    <>
      <div className="search-section">
        <SubmittedBatchClaimsForm
          isFetchingSubmitted={props.isFetching}
          getSubmitted={props.getSubmitted}
          setDataSource={setSubmittedBatchClaims}
        />
      </div>
      <div className="Content">
        { submittedBatchClaims.length === ClaimList.SubmittedBatchClaimMaxLimit && 
          <p>Search results are limited to recent 100 items. If you dont find what you were looking for, try again using more specific search terms.</p>
        }        
        <Table
          columns={columns}
          dataSource={submittedBatchClaims}
          pagination={{ position: ['bottomRight'], showSizeChanger: true }}
          loading={props.isFetching}
        />
      </div>
    </>
  );
}

const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingSubmissionFile: store.rootReducer.claim.isFetchingSubmission,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
{
  getSubmissionFileUsingEdifecs,
  getSubmissionFile,
},
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(SubmittedBatchClaimsTable);